import { useContext } from 'react';
import Markdown from 'react-markdown';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { RecipientPopover } from '@bonusly/common-recipient-popover';
import {
	Link,
	LinkProps,
	ListItem,
	OrderedList,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
	Text,
	UnorderedList,
} from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';
import { useBuildRecognizeURL } from '@bonusly/url';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { useHomepageRedesignEnabled } from '../../../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { useHashtagLink } from '../../../../../modules/bonus-feed/features/recognition/hooks/useHashtagLink';
import { AppPath } from '../../../../../modules/top-navbar/config/router';
import { pathWithId } from '../../../../../modules/top-navbar/utils/api';
import { Bonus } from '../../../../../types/bonus';
import { isDefaultCelebration } from '../../../../../utils/bonus';
import { populateEmojis } from '../../../../../utils/emoji';
import { BonusTextContext } from '../../components/bonus/bonus-text';
import { Chip } from '../../components/common/chip';
import { HashtagDetail } from '../../components/common/hashtag/hashtag-detail';
import { normalizeLink } from '../../components/common/link/utils';
import { Route } from '../../config/router';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from '../../hooks/use-bonus-feed-item-analytics';
import { useHashtags } from '../../hooks/use-hashtags';

export const Amount = (text: string) => {
	const bonus = useContext(BonusTextContext);

	let styles = {};

	if (bonus && isDefaultCelebration(bonus)) {
		styles = {
			backgroundColor: 'none',
			border: '1px solid',
			borderColor: 'points.fg',
			color: 'points.fg',
		};
	} else {
		styles = {
			backgroundColor: 'points.bgAlpha',
			color: 'points.fg',
		};
	}

	return (
		<Chip data-testid="amount-chip" as="span" size="xs" {...styles}>
			{text}
		</Chip>
	);
};

const StyleLink = (props: LinkProps) => {
	return <Link color="text.interactive" {...props} />;
};

export const NewLine = () => <br />;

export const TextString = (text: string) => {
	// The markdown component doesn't render new lines as expected, so we need to replace them with a space and a new line
	text = text.replace(/\n/gi, '&nbsp;\n');

	return (
		<>
			{/*
			The Markdown component unavoidably strips out trailing whitespace and new lines from the start of a string.
			If the string includes initial whitespace we need to manually add a space/new line to be rendered.
			*/}
			{text.match(/^\n/) && '\n'}
			{text.match(/^ /) && ' '}
			<Markdown
				components={{
					a: StyleLink,
					p: /* istanbul ignore next - Markdown component is mocked */ ({ children }) => <>{children}</>,
					// Override react-markdown ul/ol elements to add class names
					// discard ordered prop to avoid console warning from react-markdown
					/* eslint-disable @typescript-eslint/no-unused-vars */
					ol: ({ ordered, ...props }) => <OrderedList {...props} />,
					ul: ({ ordered, ...props }) => <UnorderedList {...props} />,
					li: ({ ordered, ...props }) => <ListItem {...props} />,
					/* eslint-enable @typescript-eslint/no-unused-vars */
					pre: ({ children }) => <pre style={{ whiteSpace: 'pre-line' }}>{children}</pre>,
					code: ({ children }) => <code style={{ whiteSpace: 'pre-line' }}>{children}</code>,
				}}
				linkTarget="_blank"
				transformLinkUri={normalizeLink}
			>
				{populateEmojis(text)}
			</Markdown>
			{/*
        The Markdown component unavoidably strips out trailing whitespace and new lines from the end of a string also 🤡
        If the string includes trailing whitespace we need to manually add a space/new line to be rendered.
      */}
			{text.match(/\n$/) && '\n'}
			{text.match(/ $/) && ' '}
		</>
	);
};

const createUserSegment =
	<T extends keyof Pick<Bonus, 'receivers' | 'tagged_users'>>(attr: T, color: string, event: BonusFeedTrackedAction) =>
	// eslint-disable-next-line react/display-name
	(text: string) => {
		const bonus = useContext(BonusTextContext);
		const isNewProfileEnabled = useFeatureFlag('new-profile-page');
		const recognizeURL = useBuildRecognizeURL();

		const trackClick = useTrackBonusFeedItemEvent(bonus as Bonus, event);
		const textUsername = text.substring(1);

		// User is used just for cc users
		const user = bonus?.[attr]?.find(({ all_usernames, username }) => {
			// Check all_usernames if available, otherwise fall back to username
			return all_usernames?.some((u) => u === textUsername) || username === textUsername;
		});

		const userToken = bonus?.reason_tokens?.find(({ type, token }) => {
			const userTypes = ['username'];
			return userTypes.includes(type) && token === text;
		});

		const group = bonus?.reason_tokens?.find(({ type, token }) => {
			const groupTypes = ['everyone', 'department', 'location', 'team', 'csv_upload'];
			return groupTypes.includes(type) && token === text;
		});

		if (group) {
			return (
				<RecipientPopover recipient={Recipient.fromReasonToken(group, bonus?.[attr] || [])} openDelay={500} placement="top">
					<Text as="span" role="button" tabIndex={0} color={color} cursor="pointer" fontWeight="medium" size="lg" _hover={{ textDecor: 'underline' }}>
						{text}
					</Text>
				</RecipientPopover>
			);
		}

		if (!userToken && !user) return TextString(text);

		const userId = userToken?.user_ids?.[0] || user?.id || '';

		return (
			<RecipientPopover
				recipient={
					new Recipient({
						id: userId,
						display_name: '',
						profile_pic_url: '',
						username: userToken?.token?.substring(1) || user?.username || '',
					})
				}
				openDelay={500}
				placement="top"
			>
				{isNewProfileEnabled ? (
					<Link
						as={RouterLink}
						to={pathWithId(AppPath.Profile, userId)}
						color={color}
						fontWeight="medium"
						onClick={(e) => {
							trackClick();
							e.stopPropagation();
						}}
					>
						{textUsername === (userToken?.token?.substring(1) || user?.username)
							? text
							: text.substring(0, 1) + (userToken?.token?.substring(1) || user?.username)}
					</Link>
				) : (
					<Link
						color={color}
						fontWeight="medium"
						onClick={(e) => {
							trackClick();
							e.stopPropagation();
						}}
						href={recognizeURL(pathWithId(Route.UserProfile, userId))}
					>
						{textUsername === (userToken?.token?.substring(1) || user?.username)
							? text
							: text.substring(0, 1) + (userToken?.token?.substring(1) || user?.username)}
					</Link>
				)}
			</RecipientPopover>
		);
	};

export const Mention = createUserSegment('receivers', 'text.interactive', BonusFeedTrackedAction.RecipientClick);

export const UserTag = createUserSegment('tagged_users', 'text.softest', BonusFeedTrackedAction.TaggedUserClick);

export const Hashtag = (text: string) => {
	const hashtagLinkUrl = useHashtagLink(text);
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();
	const navigate = useNavigate();
	const { data } = useHashtags({
		params: {
			display_format: 'core_value_hashtags',
		},
	});

	const core_value = data?.data.result.values.find(({ value }) => value === text);
	const hashtag = {
		core_value,
		hashtag: text,
	};

	const handleClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		navigate(hashtagLinkUrl);
		window.scrollTo(0, 0);
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			event.stopPropagation();
			navigate(hashtagLinkUrl);
			window.scrollTo(0, 0);
		}
	};

	return (
		<Popover isLazy openDelay={500} placement="top" trigger="hover">
			<PopoverTrigger>
				{isHomepageRedesignEnabled ? (
					<Text
						as="span"
						role="button"
						tabIndex={0}
						cursor="pointer"
						color="hashtag.fg"
						fontSize={150}
						fontWeight="medium"
						wordBreak="break-word"
						_hover={{ color: 'hashtag.fgHover' }}
						onClick={handleClick}
						onKeyDown={handleKeyDown}
					>
						{text}
					</Text>
				) : (
					<Link
						href={hashtagLinkUrl}
						color="hashtag.fg"
						fontSize={150}
						fontWeight="medium"
						wordBreak="break-word"
						_hover={{ color: 'hashtag.fgHover' }}
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						{text}
					</Link>
				)}
			</PopoverTrigger>
			<Portal>
				<PopoverContent borderColor="bg.subtle" boxShadow="xl">
					<PopoverBody p={5}>
						<HashtagDetail hashtag={hashtag} />
					</PopoverBody>
				</PopoverContent>
			</Portal>
		</Popover>
	);
};
